
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Bookmark, Home, FileText, Shield, Server, Cloud, ClipboardList, BookOpen } from 'lucide-react';
import Introduction from './Introduction';
import TechnologyRequirements from './TechnologyRequirements';
import KeyRequirements from './KeyRequirements';
import TechnicalSafeguards from './TechnicalSafeguards';
import CloudVendorSolutions from './CloudVendorSolutions';
import Documentation from './Documentation';
import ComplianceProgram from './ComplianceProgram';
import Conclusion from './Conclusion';

const HIPAACompliance: React.FC = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      {/* Quick Navigation */}
      <div className="bg-itmgmt-light-gray rounded-lg p-4 mb-8 sticky top-20 z-10 shadow-sm">
        <h4 className="flex items-center text-lg font-semibold mb-3 text-itmgmt-dark-blue">
          <Bookmark className="w-4 h-4 mr-2" />
          Quick Navigation
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { id: 'intro', label: 'Introduction', icon: <Home className="w-3 h-3 mr-1" /> },
            { id: 'tech-requirements', label: 'Technology Requirements', icon: <Server className="w-3 h-3 mr-1" /> },
            { id: 'key-requirements', label: 'Key Requirements', icon: <Shield className="w-3 h-3 mr-1" /> },
            { id: 'technical-safeguards', label: 'Technical Safeguards', icon: <FileText className="w-3 h-3 mr-1" /> },
            { id: 'cloud-solutions', label: 'Cloud Solutions', icon: <Cloud className="w-3 h-3 mr-1" /> },
            { id: 'documentation', label: 'Documentation', icon: <ClipboardList className="w-3 h-3 mr-1" /> },
            { id: 'compliance-program', label: 'Compliance Program', icon: <BookOpen className="w-3 h-3 mr-1" /> }
          ].map((item) => (
            <button 
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="flex items-center text-xs md:text-sm text-itmgmt-medium-blue hover:text-itmgmt-bright-blue px-2 py-1 rounded hover:bg-blue-50 transition-colors"
            >
              {item.icon} {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div id="intro">
        <Introduction />
      </div>
      
      <div id="tech-requirements">
        <TechnologyRequirements />
      </div>
      
      <div id="key-requirements">
        <KeyRequirements />
      </div>
      
      <div id="technical-safeguards">
        <TechnicalSafeguards />
      </div>
      
      <div id="cloud-solutions">
        <CloudVendorSolutions />
      </div>
      
      <div id="documentation">
        <Documentation />
      </div>
      
      <div id="compliance-program">
        <ComplianceProgram />
      </div>
      
      <Conclusion />
      
      <div className="bg-itmgmt-light-gray p-6 rounded-lg mt-8 mb-6">
        <h3 className="text-xl font-semibold mb-3 text-itmgmt-dark-blue">Need Help with HIPAA Compliance?</h3>
        <p className="mb-4 text-gray-700 leading-relaxed">
          Our healthcare IT specialists can help you navigate HIPAA requirements and implement the technical safeguards your practice needs. Contact us today for a confidential compliance assessment.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button asChild className="mt-2">
            <Link to="/book">Schedule a HIPAA Assessment</Link>
          </Button>
          <Button variant="outline" asChild className="mt-2">
            <Link to="/services/cybersecurity">Learn About Our Cybersecurity Services</Link>
          </Button>
        </div>
      </div>

      {/* Related Reading Section */}
      <div className="mt-10 pt-6 border-t border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-itmgmt-dark-blue">Related Reading</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <h4 className="font-semibold mb-2 text-itmgmt-medium-blue">
              <Link to="/blog/cybersecurity-best-practices-small-business" className="hover:text-itmgmt-bright-blue">
                Cybersecurity Best Practices for Small Businesses
              </Link>
            </h4>
            <p className="text-sm text-gray-600 mb-3">Learn essential cybersecurity measures that also support HIPAA compliance.</p>
            <Link to="/blog/cybersecurity-best-practices-small-business" className="text-xs text-itmgmt-bright-blue font-medium hover:underline">Read Article →</Link>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <h4 className="font-semibold mb-2 text-itmgmt-medium-blue">
              <Link to="/blog/zero-trust-security-small-businesses" className="hover:text-itmgmt-bright-blue">
                Zero Trust Security for Small Businesses
              </Link>
            </h4>
            <p className="text-sm text-gray-600 mb-3">Discover how Zero Trust security models can strengthen your HIPAA compliance posture.</p>
            <Link to="/blog/zero-trust-security-small-businesses" className="text-xs text-itmgmt-bright-blue font-medium hover:underline">Read Article →</Link>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <h4 className="font-semibold mb-2 text-itmgmt-medium-blue">
              <Link to="/blog/cost-of-it-downtime-prevention" className="hover:text-itmgmt-bright-blue">
                The True Cost of IT Downtime
              </Link>
            </h4>
            <p className="text-sm text-gray-600 mb-3">Understand how downtime affects healthcare practices and patient data security.</p>
            <Link to="/blog/cost-of-it-downtime-prevention" className="text-xs text-itmgmt-bright-blue font-medium hover:underline">Read Article →</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default HIPAACompliance;
